import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  login,
  logout,
  refreshSession,
  retrySession,
  setStatus,
  authStore,
} from 'services/auth/auth-store';
import type { AuthState } from 'services/auth/auth-store';
import { OAUTH_REFRESH_TOKEN } from 'services/auth/constants';
import { startSessionLifecycle } from 'services/auth/session';

// Paths that must neither entry-refresh nor redirect (OAuth callbacks and public flows).
const SKIP_PATHS = ['/oauth/callback', '/oauth/start', '/document_request', '/reset-password', '/unsubscribe'];

const isSkipPath = (pathname: string): boolean => SKIP_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export type AuthContextValue = AuthState & {
	login: typeof login;
	logout: typeof logout;
	retrySession: typeof retrySession;
};

export const useAuth = (): AuthContextValue => {
  const [state, setState] = useState<AuthState>(authStore.getState());

  useEffect(() => {
    const unsubscribe = authStore.subscribe(setState);
    // Resync: an update may have landed between the first render and this effect.
    setState(authStore.getState());
    return unsubscribe;
  }, []);

  return {
    ...state,
    login,
    logout,
    retrySession,
  };
};

type AuthProviderProps = { children: ReactNode };

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const entryRan = useRef(false);
  const mounted = useRef(true);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    startSessionLifecycle();
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (entryRan.current) return;
    if (isSkipPath(pathname)) {
      // No stored session on a public path: nothing to refresh, so consumers can leave `loading`.
      if (!localStorage.getItem(OAUTH_REFRESH_TOKEN)) setStatus('anonymous');
      return;
    }
    entryRan.current = true;
    if (!localStorage.getItem(OAUTH_REFRESH_TOKEN)) {
      setStatus('anonymous');
      return;
    }
    refreshSession().then((status) => {
      if (status === 'anonymous' && mounted.current && !isSkipPath(pathnameRef.current)) history.replace('/sign-in');
    });
  }, [pathname, history]);

  return <>{children}</>;
};
